/**
 * Admin · App → «Voti caricati» (Andrea 2026-10-16).
 *
 * Elenco di tutti i giocatori che hanno preso voto nella giornata scelta
 * (menu a tendina), con bonus e malus, modificabili uno per uno: voto base,
 * gol, gol subiti, autogol, ammonizioni, espulsioni, assist, porta
 * inviolata, rigori. Ogni correzione passa da PATCH /admin/voti/{id}: il
 * backend crea una nuova versione (append-only) e la propaga alle giornate
 * sorelle delle altre leghe, come l'import.
 *
 * Pagina a se', non dentro «Calcolo giornata»: quella e' una pagina di
 * lega, mentre i voti sono comuni a tutte le leghe (admin dell'app).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  type ModificaVotoPayload,
  useModificaVoto,
  useVotiGiornataAdmin,
  type VotoAdminItem,
} from "../../hooks/useFase4";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { GiornataSelector } from "../common/GiornataSelector";
import { siglaRuolo, sigleRuoli } from "../common/nomenclatura";
import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";

const INPUT =
  "w-full px-2 py-1 bg-primary-deep border border-primary-soft rounded text-fg text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-accent";
const SELECT =
  "px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent";

/** Colonne bonus/malus: chiave, etichetta corta, titolo esteso, massimo. */
const CONTATORI: { k: keyof ModificaVotoPayload; label: string; titolo: string; max: number }[] = [
  { k: "gol_fatti", label: "G", titolo: "Gol fatti", max: 9 },
  { k: "assist", label: "A", titolo: "Assist", max: 9 },
  { k: "gol_subiti", label: "GS", titolo: "Gol subiti", max: 15 },
  { k: "autogol", label: "AG", titolo: "Autogol", max: 5 },
  { k: "ammonizioni", label: "Amm", titolo: "Ammonizioni", max: 2 },
  { k: "espulsioni", label: "Esp", titolo: "Espulsioni", max: 1 },
  { k: "rigore_segnato", label: "R+", titolo: "Rigori segnati", max: 5 },
  { k: "rigore_sbagliato", label: "R-", titolo: "Rigori sbagliati", max: 5 },
  { k: "rigore_parato", label: "RP", titolo: "Rigori parati", max: 5 },
];

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0] && typeof d[0] === "object" && "msg" in d[0]) {
    return String((d[0] as { msg: string }).msg);
  }
  return err.message ?? tr("Errore");
}

function ruolo(v: VotoAdminItem): string {
  if (v.ruoli_mantra && v.ruoli_mantra.length > 0) return sigleRuoli(v.ruoli_mantra);
  return siglaRuolo(v.ruolo_classic);
}

/** Riga in modifica: stato locale dei campi come stringhe (input). */
interface Bozza {
  voto_base: string;
  contatori: Record<string, string>;
  porta_inviolata: boolean;
}

function bozzaDa(v: VotoAdminItem): Bozza {
  return {
    voto_base: v.voto_base ?? "",
    contatori: Object.fromEntries(
      CONTATORI.map((c) => [c.k, String(v[c.k as keyof VotoAdminItem])]),
    ),
    porta_inviolata: v.porta_inviolata,
  };
}

/** Solo i campi davvero cambiati rispetto al voto attuale. */
function differenze(v: VotoAdminItem, b: Bozza): ModificaVotoPayload {
  const out: ModificaVotoPayload = {};
  const votoNuovo = b.voto_base.trim().replace(",", ".");
  const votoVecchio = v.voto_base ?? "";
  if (votoNuovo !== votoVecchio && !(votoNuovo === "" && v.voto_base === null)) {
    out.voto_base = votoNuovo === "" ? null : votoNuovo;
  }
  for (const c of CONTATORI) {
    const n = Number(b.contatori[c.k] || 0);
    if (n !== v[c.k as keyof VotoAdminItem]) (out as Record<string, number>)[c.k] = n;
  }
  if (b.porta_inviolata !== v.porta_inviolata) out.porta_inviolata = b.porta_inviolata;
  return out;
}

export function VotiGiornataPage() {
  const { user } = useAuth();
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneId = stagioneAttiva?.id ?? "";
  const [giornataId, setGiornataId] = useState("");
  const [cerca, setCerca] = useState("");
  const [club, setClub] = useState("");
  const [tipo, setTipo] = useState("");
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [bozza, setBozza] = useState<Bozza | null>(null);
  const [esito, setEsito] = useState<{ ok: boolean; testo: string } | null>(null);

  const { data, isLoading } = useVotiGiornataAdmin(giornataId || null);
  const modifica = useModificaVoto();

  const clubs = useMemo(
    () =>
      Array.from(new Set((data?.voti ?? []).map((v) => v.club_serie_a ?? "")))
        .filter(Boolean)
        .sort(),
    [data],
  );
  const righe = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    return (data?.voti ?? []).filter(
      (v) =>
        (!q || v.nome.toLowerCase().includes(q)) &&
        (!club || v.club_serie_a === club) &&
        (!tipo || v.tipo === tipo),
    );
  }, [data, cerca, club, tipo]);

  const apri = (v: VotoAdminItem) => {
    setInModifica(v.id);
    setBozza(bozzaDa(v));
    setEsito(null);
  };
  const chiudi = () => {
    setInModifica(null);
    setBozza(null);
  };
  const salva = async (v: VotoAdminItem) => {
    if (!bozza) return;
    const payload = differenze(v, bozza);
    if (Object.keys(payload).length === 0) {
      chiudi();
      return;
    }
    try {
      await modifica.mutateAsync({ votoId: v.id, giornataId, payload });
      setEsito({ ok: true, testo: `${tr("Voto corretto")}: ${v.nome}` });
      chiudi();
    } catch (e) {
      setEsito({ ok: false, testo: errMsg(e) });
    }
  };

  if (user && !user.is_admin_app) {
    return (
      <MobileShell logoSuffix="VOTI">
        <div className="card text-sm text-fg-muted">
          {tr("I voti li carica l'amministratore dell'app, una volta per tutte le leghe.")}
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell logoSuffix="VOTI">
      <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Voti caricati")}</h2>
      <div className="card text-[11px] text-fg-muted">
        {tr(
          "Tutti i giocatori che hanno preso voto nella giornata scelta, con bonus e malus. Ogni correzione crea una nuova versione del voto (la vecchia resta nello storico) e passa da sola alle giornate delle altre leghe.",
        )}
      </div>

      <div className="card grid grid-cols-1 md:grid-cols-4 gap-3">
        <GiornataSelector
          stagioneId={stagioneId}
          value={giornataId}
          onChange={(id) => {
            setGiornataId(id);
            chiudi();
            setEsito(null);
          }}
        />
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
            {tr("Cerca giocatore")}
          </label>
          <input
            className={`${SELECT} w-full`}
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder={tr("Nome…")}
          />
        </div>
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
            {tr("Club")}
          </label>
          <select
            className={`${SELECT} w-full`}
            value={club}
            onChange={(e) => setClub(e.target.value)}
          >
            <option value="">{tr("Tutti i club")}</option>
            {clubs.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
            {tr("Tipo")}
          </label>
          <select
            className={`${SELECT} w-full`}
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">{tr("Tutti i tipi")}</option>
            <option value="finale">{tr("Finale")}</option>
            <option value="live">{tr("Live")}</option>
          </select>
        </div>
      </div>

      {data?.giornata_stato === "calcolata" && (
        <div className="card text-xs text-warning border-warning/40">
          ⚠{" "}
          {tr(
            "Questa giornata è già calcolata: dopo una correzione rifai il calcolo da Calcolo giornata → Ricalcola.",
          )}{" "}
          <Link to="/giornata?tab=calcola" className="text-accent font-bold">
            {tr("Calcolo giornata")} →
          </Link>
        </div>
      )}
      {esito && (
        <div className={`text-xs ${esito.ok ? "text-success" : "text-danger"}`}>
          {esito.ok ? "✓ " : "⚠ "}
          {esito.testo}
        </div>
      )}

      {!giornataId && (
        <div className="card text-sm text-fg-muted">
          {tr("Seleziona una giornata per vedere i voti.")}
        </div>
      )}
      {giornataId && isLoading && (
        <div className="card text-sm text-fg-muted">{tr("Caricamento…")}</div>
      )}
      {giornataId && data && data.voti.length === 0 && (
        <div className="card text-sm text-fg-muted">
          {tr("Nessun voto caricato per questa giornata.")}
        </div>
      )}
      {giornataId && data && data.voti.length > 0 && (
        <div className="card p-0 overflow-x-auto">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-fg-muted border-b border-primary-soft flex justify-between">
            <span>{tr("{{v1}} voti", { v1: righe.length })}</span>
            <span>{tr("Vuoto = senza voto (SV)")}</span>
          </div>
          {righe.length === 0 && (
            <div className="p-4 text-sm text-fg-muted">
              {tr("Nessun voto corrisponde ai filtri.")}
            </div>
          )}
          {righe.length > 0 && (
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-primary-deep/40 text-[10px] uppercase tracking-wider text-fg-muted">
                  <th className="text-left px-3 py-2">{tr("Giocatore")}</th>
                  <th className="text-left px-2 py-2">{tr("Club")}</th>
                  <th className="text-right px-2 py-2">{tr("Voto")}</th>
                  {CONTATORI.map((c) => (
                    <th key={c.k} className="text-right px-2 py-2" title={tr(c.titolo)}>
                      {c.label}
                    </th>
                  ))}
                  <th className="text-center px-2 py-2" title={tr("Porta inviolata")}>
                    PI
                  </th>
                  <th className="text-left px-2 py-2">{tr("Tipo")}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {righe.map((v) => {
                  const editing = inModifica === v.id && bozza;
                  return (
                    <tr
                      key={v.id}
                      className={`border-t border-primary/10 ${editing ? "bg-accent/5" : ""}`}
                    >
                      <td className="px-3 py-1.5">
                        <div className="font-bold text-fg">{v.nome}</div>
                        <div className="text-[10px] text-fg-muted font-mono">
                          {ruolo(v)} · v{v.versione} · {v.fonte}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-fg-muted">{v.club_serie_a ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">
                        {editing ? (
                          <input
                            className={`${INPUT} w-16`}
                            inputMode="decimal"
                            value={bozza.voto_base}
                            onChange={(e) => setBozza({ ...bozza, voto_base: e.target.value })}
                            placeholder={tr("SV")}
                          />
                        ) : v.voto_base === null ? (
                          <span className="text-fg-muted">{tr("SV")}</span>
                        ) : (
                          <span className="font-bold">{v.voto_base}</span>
                        )}
                      </td>
                      {CONTATORI.map((c) => {
                        const val = v[c.k as keyof VotoAdminItem] as number;
                        return (
                          <td key={c.k} className="px-2 py-1.5 text-right font-mono">
                            {editing ? (
                              <input
                                className={`${INPUT} w-12`}
                                inputMode="numeric"
                                type="number"
                                min={0}
                                max={c.max}
                                value={bozza.contatori[c.k]}
                                onChange={(e) =>
                                  setBozza({
                                    ...bozza,
                                    contatori: { ...bozza.contatori, [c.k]: e.target.value },
                                  })
                                }
                              />
                            ) : (
                              <span className={val ? "text-fg" : "text-fg-muted/50"}>{val}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={bozza.porta_inviolata}
                            onChange={(e) =>
                              setBozza({ ...bozza, porta_inviolata: e.target.checked })
                            }
                          />
                        ) : v.porta_inviolata ? (
                          "✓"
                        ) : (
                          <span className="text-fg-muted/50">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-[10px] uppercase text-fg-muted">{v.tipo}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {editing ? (
                          <span className="inline-flex gap-1">
                            <button
                              type="button"
                              className="btn-primary text-xs px-2 py-1 disabled:opacity-50"
                              disabled={modifica.isPending}
                              onClick={() => salva(v)}
                            >
                              {modifica.isPending ? tr("Salvo…") : tr("Salva")}
                            </button>
                            <button
                              type="button"
                              className="btn-ghost text-xs px-2 py-1"
                              disabled={modifica.isPending}
                              onClick={chiudi}
                            >
                              {tr("Annulla")}
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost text-xs px-2 py-1"
                            onClick={() => apri(v)}
                          >
                            ✏️ {tr("Modifica")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </MobileShell>
  );
}
